'use client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { getFileExtension, getOriginalFilename, resolveFileAsset } from '@/sanity/lib/file';
import { processHeading, type HeadingType } from '@workspace/lib/heading-utils';
import { INVISIBLES } from '@workspace/constants';
import { AccordionItem, AccordionLink } from '@workspace/interfaces';

// Type definitions
interface SanityImage {
  asset: {
    _ref: string;
    _type: 'reference';
  };
  alt?: string;
  hotspot?: any;
  crop?: any;
}

interface SanityFile {
  asset: {
    _ref: string;
    _type: 'reference';
    url?: string;
    originalFilename?: string;
    label?: string;
  };
}

interface SanityDocumentItem {
  _key: string;
  label: string;
  linkType: 'external' | 'file';
  externalUrl?: string;
  file?: SanityFile;
}

interface SanityDocumentSection {
  _key: string;
  isVisible?: boolean;
  heading?: string;
  documentItems: SanityDocumentItem[];
}

interface DocumentBlockData {
  title?: HeadingType;
  image?: SanityImage;
  customBackgroundColor?: string | { hex: string };
  documentSections: SanityDocumentSection[];
  defaultExpanded?: string[];
  imagePosition?: 'left' | 'right' | 'hidden';
  isEnabled?: boolean;
}

interface FileUrlInfo {
  url: string;
  filename: string;
  extension: string;
}

interface UseDocumentBlockReturn {
  // Component state
  fileUrls: Record<string, FileUrlInfo>;

  // Processed data
  resolvedTitleText: string;
  cleanImagePosition: string;
  visibleSections: SanityDocumentSection[];
  transformedAccordionItems: AccordionItem[];
  defaultExpandedIds: string[];

  // Computed properties
  shouldRender: boolean;
  hasVisibleSections: boolean;
}

/**
 * Custom hook for managing DocumentBlock component logic
 * Handles file URL resolution, background color processing, and data transformation
 */
export function useDocumentBlock(block: DocumentBlockData): UseDocumentBlockReturn {
  const { title, documentSections, defaultExpanded = [], imagePosition = 'left', isEnabled = true } = block;

  // State
  const [fileUrls, setFileUrls] = useState<Record<string, FileUrlInfo>>({});

  // Memoized processed title
  const { text: resolvedTitleText } = useMemo(() => {
    return processHeading(title);
  }, [title]);

  // Memoized cleaned image position
  const cleanImagePosition = useMemo(() => {
    return typeof imagePosition === 'string' ? imagePosition.replace(INVISIBLES, '').trim().toLowerCase() : 'left';
  }, [imagePosition]);

  // Memoized visible sections
  const visibleSections = useMemo(() => {
    return documentSections?.filter((section: SanityDocumentSection) => section.isVisible !== false) || [];
  }, [documentSections]);

  // Computed properties
  const shouldRender = isEnabled;

  const hasVisibleSections = visibleSections.length > 0;

  // File URL resolution logic
  const getFileUrls = useCallback(async (sections: SanityDocumentSection[]): Promise<Record<string, FileUrlInfo>> => {
    const urls: Record<string, FileUrlInfo> = {};

    for (const section of sections) {
      // Only process visible sections
      if (section.isVisible === false) continue;

      for (const item of section.documentItems || []) {
        if (item.file?.asset) {
          try {
            // Resolve the file asset to get the URL
            const url = resolveFileAsset(item.file.asset);

            // Get the file extension from the URL or reference
            let extension = '';
            if (item.file.asset._ref) {
              extension = getFileExtension(item.file.asset._ref);
            } else if (url && url !== '#') {
              const urlParts = url.split('.');
              extension = urlParts[urlParts.length - 1] || '';
            }

            // Get the original filename or use the label as fallback
            const filename = getOriginalFilename(item.file) || item.label || `download.${extension}`;

            urls[item._key] = {
              url,
              filename,
              extension,
            };
          } catch (error) {
            console.error('Error resolving file URL:', error);
            urls[item._key] = {
              url: '#',
              filename: 'download',
              extension: '',
            };
          }
        }
      }
    }

    return urls;
  }, []);

  // Effect for file URL resolution
  useEffect(() => {
    if (documentSections?.length > 0) {
      getFileUrls(documentSections).then(setFileUrls);
    }
  }, [documentSections, getFileUrls]);

  // Memoized accordion items transformation
  const transformedAccordionItems = useMemo((): AccordionItem[] => {
    return (
      documentSections
        ?.filter((section: SanityDocumentSection) => section.isVisible !== false)
        ?.map((section: SanityDocumentSection) => ({
          id: section._key,
          title: section.heading ?? '',
          items:
            section?.documentItems?.map((link: SanityDocumentItem): AccordionLink => {
              if (link.linkType === 'external') {
                return {
                  id: link._key,
                  label: link.label,
                  url: link.externalUrl || '#',
                  type: 'external',
                };
              } else {
                // For file downloads, we need to pass additional metadata
                const fileInfo = fileUrls[link._key];
                return {
                  id: link._key,
                  label: link.label,
                  url: fileInfo?.url || '#',
                  type: 'file',
                  // Add file metadata for better download handling
                  metadata: {
                    filename: fileInfo?.filename || link.label,
                    extension: fileInfo?.extension || '',
                    originalFilename: link.file?.asset?.originalFilename || link.label,
                  },
                };
              }
            }) || [],
        })) || []
    );
  }, [documentSections, fileUrls]);

  // Memoized default expanded IDs
  const defaultExpandedIds = useMemo((): string[] => {
    if (defaultExpanded?.length > 0) {
      return (
        documentSections
          ?.filter(
            (item: SanityDocumentSection) =>
              item.isVisible !== false && defaultExpanded.includes(processHeading(item.heading).text),
          )
          ?.map((item) => item._key) || []
      );
    }

    // Set first panel as expanded by default if no defaultExpanded is specified
    const visibleSections = documentSections?.filter((item: SanityDocumentSection) => item.isVisible !== false);

    return visibleSections?.length > 0 ? [visibleSections[0]._key] : [];
  }, [documentSections, defaultExpanded]);

  return {
    // Component state
    fileUrls,

    // Processed data
    resolvedTitleText,
    cleanImagePosition,
    visibleSections,
    transformedAccordionItems,
    defaultExpandedIds,

    // Computed properties
    shouldRender,
    hasVisibleSections,
  };
}
